/**
 * Generate cache key for association data
 *
 * @param objectModel - The custom object model (e.g., "custom_objects.doctors")
 * @param locationId - The location ID
 * @returns Formatted cache key string
 */
export const generateCacheKey = (objectModel?: string, locationId?: string): string => {
  return `${objectModel}_${locationId}`;
};

/**
 * Format cardinality string from API response to human-readable format
 *
 * @param cardinality - The cardinality string from API (e.g., "MANY_TO_MANY")
 * @param fallbackText - Fallback text if cardinality is empty
 * @returns Formatted cardinality string (e.g., "Many to Many")
 *
 * Logic flow:
 * 1. Replace underscores with spaces
 * 2. Split into words
 * 3. Capitalize first letter of each word, lowercase the rest
 * 4. Join with spaces
 */
export const formatCardinality = (cardinality?: string, fallbackText = "Many to Many"): string => {
  if (!cardinality) {
    return fallbackText;
  }

  return cardinality
    .replace(/_/g, " ")
    .split(" ")
    .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
    .join(" ");
};

/**
 * Get custom object labels from store
 *
 * @param customObjects - Array of custom objects from store
 * @param objectModel - The object model to find labels for
 * @returns Object labels with plural and singular forms
 */
export const getCustomObjectLabels = (customObjects: any[], objectModel?: string) => {
  if (!objectModel) {
    return { plural: "Contacts", singular: "Contact" };
  }

  const customObject = customObjects?.find((obj) => obj.key === objectModel);
  return customObject?.labels || { plural: "Contacts", singular: "Contact" };
};

/**
 * Set default association selection based on saved settings or first available option
 *
 * @param options - Available association options
 * @param savedAssociation - Previously saved association settings
 * @param currentObjectModel - Current object model being configured
 * @returns Selected association ID or empty string
 *
 * Logic flow:
 * 1. Check if options are available
 * 2. Look for saved association matching current object model
 * 3. Verify saved association exists in current options
 * 4. Return saved association or first available option
 */
export const getDefaultAssociationSelection = (
  options: any[],
  savedAssociation: any,
  currentObjectModel?: string
): string => {
  if (!options || options.length === 0) {
    return "";
  }

  // Check if there's a saved association for this object model
  if (
    savedAssociation?.objectModel === currentObjectModel &&
    savedAssociation?.selectedAssociation
  ) {
    // Handle both string and string[] shapes for selectedAssociation
    const savedSelection = Array.isArray(savedAssociation.selectedAssociation)
      ? savedAssociation.selectedAssociation[0]
      : savedAssociation.selectedAssociation;

    if (options.some((option) => option.value === savedSelection)) {
      return savedSelection;
    }
  }

  // Default to first option if no saved association or saved association not found
  return options[0]?.value || "";
};

/**
 * Check if this is the only custom object field of a specific type
 *
 * @param elements - Form elements (for forms) or null (for surveys/quizzes)
 * @param slides - Survey/quiz slides (for surveys/quizzes) or null (for forms)
 * @param objectModel - The object model to check
 * @returns Boolean indicating if this is the only field of this type
 */
export const isOnlyCustomObjectField = (
  elements: any[] | null,
  slides: any[] | null,
  objectModel?: string
): boolean => {
  if (!objectModel) {
    return false;
  }

  const customObjectFields = elements
    ? elements.filter((element) => element.model === objectModel)
    : slides?.flatMap(
        (slide) => slide.slideData?.filter((element) => element.model === objectModel) || []
      ) || [];

  return customObjectFields.length === 1;
};

/**
 * Check if any custom object fields of a specific type remain
 *
 * @param elements - Form elements (for forms) or null (for surveys/quizzes)
 * @param slides - Survey/quiz slides (for surveys/quizzes) or null (for forms)
 * @param objectModel - The object model to check
 * @returns Boolean indicating if any fields of this type remain
 */
export const hasCustomObjectFields = (
  elements: any[] | null,
  slides: any[] | null,
  objectModel?: string
): boolean => {
  if (!objectModel) {
    return false;
  }

  const customObjectFields = elements
    ? elements.filter((element) => element.model === objectModel)
    : slides?.flatMap(
        (slide) => slide.slideData?.filter((element) => element.model === objectModel) || []
      ) || [];

  return customObjectFields.length > 0;
};
